// utils/mercadoPaletas.ts — paletas de color y helpers de la DEMANDA/OCUPACIÓN
// del módulo Mercado (round 2026-07-23).
//
// FUENTE DE VERDAD VISUAL. La demanda del mercado se muestra en 5 niveles
// (muy_baja → muy_alta) y su color sale de una PALETA elegida por el admin en
// /mercado → Parámetros (`paletaDemanda`, singleton mercado_settings). El
// backend solo valida/persiste el ID de la paleta (ver PALETA_IDS en
// mercado.controller.js); los COLORES viven acá. Si agregas/quitas una paleta,
// sincroniza AMBOS lados con el MISMO id.
//
// Cada paleta son 5 hex ordenados de ocupación BAJA (índice 0 = muy_baja) a
// ALTA (índice 4 = muy_alta). Son paletas secuenciales legibles (varias de
// ColorBrewer + estilo coolors), pensadas para leerse como una rampa "poca →
// mucha demanda".
import type { MercadoDemanda } from "../services/mercado.api";

export interface MercadoPaleta {
  id: string;
  nombre: string;
  /** 5 colores, índice 0 = muy_baja … índice 4 = muy_alta. */
  colores: [string, string, string, string, string];
}

/** 15 paletas preconfiguradas (mismos ids que PALETA_IDS del backend). */
export const PALETAS: MercadoPaleta[] = [
  { id: "gris-verde", nombre: "Gris → verde", colores: ["#ebe9e9", "#fdffb7", "#f6ff43", "#a6ff80", "#00e457"] },
  { id: "verde-bosque", nombre: "Verde bosque", colores: ["#edf8e9", "#bae4b3", "#74c476", "#31a354", "#006d2c"] },
  { id: "azul-mar", nombre: "Azul mar", colores: ["#eff3ff", "#bdd7e7", "#6baed6", "#3182bd", "#08519c"] },
  { id: "naranjo-fuego", nombre: "Naranjo fuego", colores: ["#feedde", "#fdbe85", "#fd8d3c", "#e6550d", "#a63603"] },
  { id: "rojo-demanda", nombre: "Rojo demanda", colores: ["#fee5d9", "#fcae91", "#fb6a4a", "#de2d26", "#a50f15"] },
  { id: "morado", nombre: "Morado", colores: ["#f2f0f7", "#cbc9e2", "#9e9ac8", "#756bb1", "#54278f"] },
  { id: "turquesa", nombre: "Turquesa", colores: ["#e0f3f8", "#abd9e9", "#74add1", "#4575b4", "#313695"] },
  { id: "lima", nombre: "Lima", colores: ["#ffffcc", "#c2e699", "#78c679", "#31a354", "#006837"] },
  { id: "rosa-coral", nombre: "Rosa coral", colores: ["#feebe2", "#fbb4b9", "#f768a1", "#c51b8a", "#7a0177"] },
  { id: "dorado-ambar", nombre: "Dorado ámbar", colores: ["#fff7bc", "#fee391", "#fec44f", "#fe9929", "#cc4c02"] },
  { id: "menta-teal", nombre: "Menta", colores: ["#edf8fb", "#b2e2e2", "#66c2a4", "#2ca25f", "#006d2c"] },
  { id: "cielo-indigo", nombre: "Cielo índigo", colores: ["#edf8fb", "#b3cde3", "#8c96c6", "#8856a7", "#810f7c"] },
  { id: "gris-azul", nombre: "Gris neutro", colores: ["#f7f7f7", "#cccccc", "#969696", "#636363", "#252525"] },
  { id: "verde-oliva", nombre: "Verde oliva", colores: ["#ffffe5", "#f7fcb9", "#addd8e", "#41ab5d", "#006837"] },
  { id: "calido", nombre: "Cálido", colores: ["#fceade", "#f6c6a8", "#ec9a6d", "#d96941", "#9c3d1e"] },
];

export const PALETA_DEFAULT_ID = "gris-verde";

/** Niveles ascendentes: el índice coincide con el de los colores de la paleta. */
export const NIVELES_ASC: MercadoDemanda[] = ["muy_baja", "baja", "media", "alta", "muy_alta"];
/** Niveles descendentes (para la leyenda: mayor demanda primero). */
export const NIVELES_DESC: MercadoDemanda[] = ["muy_alta", "alta", "media", "baja", "muy_baja"];

/** Severidad (para elegir el día representante cuando varias estadías lo cubren). */
export const NIVEL_SEV: Record<MercadoDemanda, number> = {
  muy_baja: 1,
  baja: 2,
  media: 3,
  alta: 4,
  muy_alta: 5,
};

const NIVEL_LABEL: Record<MercadoDemanda, string> = {
  muy_alta: "Demanda muy alta",
  alta: "Demanda alta",
  media: "Demanda media",
  baja: "Demanda baja",
  muy_baja: "Demanda muy baja",
};

/** Etiqueta legible de un nivel de demanda. */
export const nivelLabel = (nivel: MercadoDemanda): string => NIVEL_LABEL[nivel];

/** Paleta por id, con fallback a la default (gris→verde) si el id no existe. */
export function getPaleta(id?: string | null): MercadoPaleta {
  return PALETAS.find((p) => p.id === id) ?? PALETAS[0];
}

/**
 * Nivel de demanda a partir de la OCUPACIÓN estimada (0..100). Buckets de 20%
 * que calzan con `demandaDeCota` del backend (que bucketea por gradiente =
 * 100 − ocupación). Cortes con `>` (no `>=`) para replicar exacto sus umbrales:
 * gradiente<20 ⇔ ocupación>80 ⇒ muy_alta; … ; gradiente≥80 ⇔ ocupación≤20 ⇒
 * muy_baja. Así color, número y demanda del backend concuerdan.
 */
export function nivelDeOcupacion(ocupacion: number): MercadoDemanda {
  if (ocupacion > 80) return "muy_alta";
  if (ocupacion > 60) return "alta";
  if (ocupacion > 40) return "media";
  if (ocupacion > 20) return "baja";
  return "muy_baja";
}

/** Color de un nivel según la paleta activa. */
export function colorDeNivel(paleta: MercadoPaleta, nivel: MercadoDemanda): string {
  return paleta.colores[NIVELES_ASC.indexOf(nivel)];
}

/** Color directo desde la ocupación (0..100) según la paleta activa. */
export function colorDeOcupacion(paleta: MercadoPaleta, ocupacion: number): string {
  return colorDeNivel(paleta, nivelDeOcupacion(ocupacion));
}

/** Ocupación (0..100) redondeada al 10% más cercano (lo que ve el CLIENTE). */
export const redondearOcupacion10 = (ocupacion: number): number =>
  Math.round(ocupacion / 10) * 10;

/** Tinte translúcido de un hex #rrggbb (para pintar el fondo del día sin matar
 * el texto). `alpha` en 0..1. */
export function tinte(hex: string, alpha = 0.16): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Color de texto (#fff u oscuro) que MEJOR contrasta con un fondo hex, según
 * la fórmula de luminancia relativa WCAG 2.x. Elige entre blanco y casi-negro
 * el de mayor razón de contraste. Garantiza legibilidad del texto sobre
 * cualquier color de la paleta (lineamiento del owner 2026-07-23).
 */
export function textoContraste(hex: string): string {
  const L = luminanciaRelativa(hex);
  const contrasteBlanco = 1.05 / (L + 0.05);
  const contrasteNegro = (L + 0.05) / 0.05;
  return contrasteBlanco >= contrasteNegro ? "#ffffff" : "#111111";
}

// ── internos ────────────────────────────────────────────────────────────────

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function canalLineal(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function luminanciaRelativa(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * canalLineal(r) + 0.7152 * canalLineal(g) + 0.0722 * canalLineal(b);
}
